/**
 * Descargar un documento de la API.
 *
 * ## Por qué no es un `<a href>`
 *
 * Siete rutas del contrato devuelven **bytes y no JSON**: el recibo, el certificado
 * fiscal, el pasaporte, los diplomas. Y todas van autenticadas con `Authorization:
 * Bearer`, mientras que el token de acceso **vive en memoria** y a propósito: no
 * está en `localStorage` para que un XSS no se lo pueda llevar (`BFF_BO.md` §3.3).
 *
 * Un enlace normal no puede mandar una cabecera, así que un `<a href>` al PDF
 * recibiría un `401`. Y meter el token en la URL sería exactamente lo que §3.3
 * prohíbe: acabaría en el historial del navegador, en el registro del servidor y en
 * cualquier captura de pantalla.
 *
 * Así que se pide con el `fetch` de la sesión —que además renueva el token si hace
 * falta, igual que el resto de la aplicación—, y los bytes se abren desde un
 * `blob:`, que es una URL local que no viaja a ninguna parte.
 *
 * ## El `URL.revokeObjectURL`
 *
 * No es cosmético. Cada `createObjectURL` ata el fichero en memoria hasta que se
 * revoca o se recarga la página, así que un administrativo abriendo treinta recibos
 * una tarde se los queda todos. Se revoca al rato y no de inmediato: revocarlo antes
 * de que la pestaña nueva lo haya leído deja una pestaña en blanco.
 */
import { fetchConSesion, type OpcionesDeSesion } from '@/shared/api/sesion';
import { aProblema, type Problema } from '@/shared/api/errores';

/** Tiempo de gracia antes de soltar el `blob:`. La pestaña ya lo ha leído. */
const GRACIA_MS = 60_000;

/**
 * Pide un documento y lo abre en otra pestaña.
 *
 * Devuelve `null` si fue bien, o el problema si no. **No lanza**: quien llama a esto
 * está en un `onClick`, y una excepción ahí no la recoge nadie.
 *
 * ## La pestaña se abre antes de pedir el documento · revisión del 02-10-2026
 *
 * Hasta esta revisión se abría después, con `window.open(url, '_blank', 'noopener')`,
 * y **ningún PDF del panel se podía ver**. Con `noopener`, `window.open` devuelve
 * siempre `null` —lo dice el estándar—; el código lo tomaba por un bloqueador de
 * ventanas, soltaba el `blob:` y enseñaba «El navegador ha bloqueado la ventana»,
 * con la pestaña ya abierta y en blanco. Recibos, diplomas y el manual del back
 * office. No lo cazaba nada porque no había prueba: ahora está
 * `src/tests/shared/descargas.test.ts`.
 *
 * Ahora se abre **dentro del clic, antes de esperar a nada**: abrirla después de un
 * `await` es lo que Safari bloquea como emergente. Sin `noopener` para poder
 * llevarla luego al documento, y con el `opener` cortado a mano, que es lo único que
 * aportaba aquella opción. Si algo falla, la pestaña se cierra.
 */
export async function abrirDocumento(
  ruta: string,
  opciones: OpcionesDeSesion
): Promise<Problema | null> {
  const ventana = window.open('', '_blank');
  if (!ventana) {
    return {
      title: 'El navegador ha bloqueado la ventana',
      status: 0,
      detail:
        'El documento se abre en otra pestaña. Permite las ventanas emergentes de esta página y vuelve a intentarlo.',
    };
  }
  try {
    ventana.opener = null;
  } catch {
    // Algún navegador no deja reasignarlo; la pestaña es nuestra y vacía.
  }
  enEspera(ventana);

  const peticion = fetchConSesion(opciones);

  try {
    const respuesta = await peticion(`${opciones.base}${ruta}`, {
      headers: { accept: 'application/pdf' },
    });

    if (!respuesta.ok) {
      ventana.close();
      /*
       * Un error **sí** viene en JSON, aunque hayamos pedido un PDF: lo escribe
       * `errorHandler.ts`, que no sabe nada de la cabecera `accept`. Si ni eso se
       * puede leer, se inventa un problema con el código de estado, que es mejor
       * que un «algo ha fallado».
       */
      const cuerpo: unknown = await respuesta.json().catch(() => null);
      if (cuerpo) return aProblema(cuerpo);
      return {
        title: 'No se ha podido abrir el documento',
        status: respuesta.status,
        detail: `El servidor respondió ${respuesta.status}.`,
      };
    }

    const bytes = await respuesta.blob();
    const url = URL.createObjectURL(bytes);
    ventana.location.href = url;

    window.setTimeout(() => URL.revokeObjectURL(url), GRACIA_MS);
    return null;
  } catch (e) {
    ventana.close();
    // Aquí caen los fallos de red: el servidor caído, el wifi del polideportivo.
    return aProblema(e);
  }
}

/** Lo que se ve en la pestaña nueva mientras llega el documento. */
function enEspera(ventana: Window): void {
  try {
    ventana.document.title = 'Preparando el documento…';
    ventana.document.body.textContent = 'Preparando el documento…';
  } catch {
    // Si no se deja tocar, se queda en blanco un momento: no es un fallo.
  }
}

/**
 * Pide un fichero y lo **guarda** con su nombre, en vez de abrirlo.
 *
 * Es lo que necesitan los `?format=csv` de §P.9: un CSV abierto en una pestaña es
 * una pared de texto con comas, y lo que el club va a hacer con él es abrirlo en
 * una hoja de cálculo. Así que `<a download>` y no `window.open`.
 *
 * Y por eso tampoco hace falta el aviso de ventana bloqueada: una descarga no la
 * frena el bloqueador de emergentes.
 *
 * El nombre lo pone quien llama y no la cabecera `Content-Disposition` del servidor:
 * `informe-de-ingresos-2026-09.csv` en la carpeta de descargas dice qué es seis
 * meses después, y un `revenue.csv` no.
 */
export async function descargarFichero(
  ruta: string,
  nombre: string,
  opciones: OpcionesDeSesion
): Promise<Problema | null> {
  const peticion = fetchConSesion(opciones);

  try {
    const respuesta = await peticion(`${opciones.base}${ruta}`, {
      headers: { accept: 'text/csv' },
    });

    if (!respuesta.ok) {
      // El error viene en JSON aunque pidamos CSV: lo escribe `errorHandler.ts`.
      const cuerpo: unknown = await respuesta.json().catch(() => null);
      if (cuerpo) return aProblema(cuerpo);
      return {
        title: 'No se ha podido descargar el informe',
        status: respuesta.status,
        detail: `El servidor respondió ${respuesta.status}.`,
      };
    }

    const bytes = await respuesta.blob();
    const url = URL.createObjectURL(bytes);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    document.body.append(enlace);
    enlace.click();
    enlace.remove();

    window.setTimeout(() => URL.revokeObjectURL(url), GRACIA_MS);
    return null;
  } catch (e) {
    return aProblema(e);
  }
}
